/**
 * La bienvenida de quien acaba de crear la cuenta: dos preguntas y al prompt.
 *
 * Qué negocio tiene (se elige uno y se continúa; «Otro» pide escribirlo) y qué
 * quiere que haga su sitio (varias). Las respuestas no se escriben en el
 * prompt: viajan a la IA como contexto y ordenan los templates. «Omitir» está
 * siempre: quien ya sabe lo que quiere va directo al prompt. Ver
 * `welcomeAnswers`.
 */
import { useEffect, useRef, useState } from 'react'
import {
  IconArrowLeft,
  IconArrowRight,
  IconBarbell,
  IconBed,
  IconBriefcase,
  IconCheck,
  IconConfetti,
  IconDeviceLaptop,
  IconDots,
  IconHeartHandshake,
  IconHome,
  IconLeaf,
  IconMicrophone,
  IconPalette,
  IconSchool,
  IconShoppingBag,
  IconStethoscope,
  IconTool,
  IconToolsKitchen2,
} from '@tabler/icons-react'
import { useI18n } from '../lib/i18n'
import { GOAL_ICONS } from './goalIcons'
import {
  WELCOME_BUSINESSES,
  WELCOME_BUSINESS_LABELS,
  WELCOME_DETAIL_MAX,
  WELCOME_GOALS,
  type WelcomeAnswers,
  type WelcomeBusiness,
  type WelcomeGoal,
} from '../lib/welcomeAnswers'
import { useScrollTopOnStep } from '../lib/useScrollTopOnStep'

const BUSINESS_ICONS: Record<WelcomeBusiness, typeof IconBriefcase> = {
  services: IconBriefcase,
  restaurant: IconToolsKitchen2,
  shop: IconShoppingBag,
  wellness: IconLeaf,
  fitness: IconBarbell,
  health: IconStethoscope,
  education: IconSchool,
  realestate: IconHome,
  events: IconConfetti,
  hospitality: IconBed,
  trades: IconTool,
  portfolio: IconPalette,
  creator: IconMicrophone,
  tech: IconDeviceLaptop,
  nonprofit: IconHeartHandshake,
  other: IconDots,
}

/**
 * La miniatura de cada tarjeta: su icono en un marco, con el mismo material que
 * las de «Desde un template» y «En blanco» de la pantalla de inicio —borde de
 * un pelo y sombra baja—, y el mismo gesto al pasar: el marco se inclina.
 */
function CardArt({ Icon }: { Icon: typeof IconBriefcase }) {
  return (
    <span className="dash-welcome-art" aria-hidden="true">
      <span className="dash-welcome-art-frame">
        <Icon size={22} stroke={1.7} />
      </span>
    </span>
  )
}

function CardCopy({ title, description }: { title: string; description: string }) {
  return (
    <span className="dash-welcome-card-copy">
      <strong>{title}</strong>
      <span>{description}</span>
    </span>
  )
}


export function WelcomeWizard({ onDone, initial }: {
  /** Las respuestas, o `null` si se omitió. */
  onDone: (answers: WelcomeAnswers | null) => void
  /** Lo que ya se sabe —del ejemplo de veltica.com—, marcado de entrada: sólo se confirma. */
  initial?: { business: string; goals: string[] } | undefined
}) {
  const { t } = useI18n()
  const [step, setStep] = useState<0 | 1>(0)
  useScrollTopOnStep(step)
  const [business, setBusiness] = useState<WelcomeBusiness | null>(() => (WELCOME_BUSINESSES as readonly string[]).includes(initial?.business ?? '') ? initial!.business as WelcomeBusiness : null)
  /* «Otro»: lo escribe con sus palabras. Hace falta para continuar —sin él,
     «otro» no le dice nada a nadie—. */
  const [detail, setDetail] = useState('')
  const detailRef = useRef<HTMLInputElement>(null)
  useEffect(() => { if (business === 'other') detailRef.current?.focus() }, [business])
  const detailMissing = business === 'other' && detail.trim().length < 2
  const [goals, setGoals] = useState<WelcomeGoal[]>(() => (initial?.goals ?? []).filter((goal): goal is WelcomeGoal => (WELCOME_GOALS as readonly string[]).includes(goal)))

  const toggleGoal = (goal: WelcomeGoal) => setGoals((current) => (
    current.includes(goal) ? current.filter((item) => item !== goal) : [...current, goal]
  ))

  return (
    <section className="dash-welcome" aria-labelledby="dash-welcome-title">
      <header className="dash-welcome-head">
        <span className="dash-welcome-kicker">{t('cwa.wz.kicker', { n: step + 1, total: 2 })}</span>
        <h1 id="dash-welcome-title">{t(step === 0 ? 'welcome.business.title' : 'welcome.goals.title')}</h1>
        <p>{t(step === 0 ? 'welcome.business.lead' : 'welcome.goals.lead')}</p>
      </header>

      {step === 0 ? (
        <>
        <div className="dash-welcome-grid is-industries" role="radiogroup" aria-labelledby="dash-welcome-title">
          {WELCOME_BUSINESSES.map((key) => {
            const Icon = BUSINESS_ICONS[key]
            return (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={business === key}
              className={`dash-welcome-card${business === key ? ' is-active' : ''}`}
              /* Elegir sólo marca: se avanza con «Continuar», para poder
                 cambiar de idea antes de pasar al siguiente. */
              onClick={() => setBusiness(key)}
            >
              <CardArt Icon={Icon} />
              <CardCopy title={t(WELCOME_BUSINESS_LABELS[key])} description={t(`welcome.business.${key}.desc`)} />
            </button>
            )
          })}
        </div>
        {business === 'other' ? (
          <label className="dash-welcome-other">
            <span>{t('welcome.business.otherLabel')}</span>
            <input
              ref={detailRef}
              type="text"
              value={detail}
              maxLength={WELCOME_DETAIL_MAX}
              placeholder={t('welcome.business.otherPlaceholder')}
              onChange={(event) => setDetail(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter' && !detailMissing) setStep(1) }}
            />
          </label>
        ) : null}
        </>
      ) : (
        <div className="dash-welcome-grid is-goals" role="group" aria-labelledby="dash-welcome-title">
          {WELCOME_GOALS.map((goal) => {
            const Icon = GOAL_ICONS[goal]
            const on = goals.includes(goal)
            return (
              <button
                key={goal}
                type="button"
                aria-pressed={on}
                className={`dash-welcome-card${on ? ' is-active' : ''}`}
                onClick={() => toggleGoal(goal)}
              >
                <CardArt Icon={Icon} />
                <CardCopy title={t(`welcome.goal.${goal}`)} description={t(`welcome.goal.${goal}.desc`)} />
                <span className="dash-welcome-check" aria-hidden="true">{on ? <IconCheck size={12} stroke={3} /> : null}</span>
              </button>
            )
          })}
        </div>
      )}

      <footer className="dash-welcome-foot">
        {step === 1 ? (
          <button type="button" className="dash-welcome-link" onClick={() => setStep(0)}>
            <IconArrowLeft size={14} /> {t('cwa.back')}
          </button>
        ) : <span />}
        <span className="dash-welcome-actions">
          <button type="button" className="dash-welcome-link" onClick={() => onDone(null)}>{t('welcome.skip')}</button>
          <button
            type="button"
            className="dash-primary-btn"
            /* En el primer paso hace falta haber elegido; en el segundo, no:
               ninguna casilla también es una respuesta. */
            disabled={step === 0 && (!business || detailMissing)}
            onClick={() => (step === 0
              ? setStep(1)
              : onDone({ business, goals, ...(business === 'other' ? { businessDetail: detail.trim() } : {}) }))}
          >
            {t('auth.continue')} <IconArrowRight size={14} />
          </button>
        </span>
      </footer>
    </section>
  )
}
